import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Save } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import { AUDIENCE_FLAGS, type FlagRow, useFeatureFlags } from '@/lib/feature-flags';

const isAudienceFlag = (key: string): boolean => (AUDIENCE_FLAGS as readonly string[]).includes(key);
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/admin/flags')({
  component: FlagsPage,
});

function FlagsPage() {
  const { data: flags, isLoading, isError, error } = useFeatureFlags();

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl space-y-6 p-6">
        <div>
          <h1 className="text-xl font-bold text-foreground">Feature Flags</h1>
          <p className="text-sm text-muted-foreground">
            Runtime toggles. Changes take effect on the next page load for all users — no redeploy.
          </p>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {isError && (
          <p className="text-sm text-[hsl(var(--danger))]">
            {error instanceof Error ? error.message : 'Failed to load flags.'}
          </p>
        )}

        {flags?.length === 0 && !isLoading && (
          <p className="text-sm text-muted-foreground">No flags defined.</p>
        )}

        <div className="space-y-3">
          {flags?.map((flag) => <FlagRowCard key={flag.key} flag={flag} />)}
        </div>
      </div>
    </div>
  );
}

function FlagRowCard({ flag }: { flag: FlagRow }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState(flag.enabled);
  const [value, setValue] = useState(flag.value);

  // Re-sync local edit state if the server row changes underneath us.
  useEffect(() => {
    setEnabled(flag.enabled);
    setValue(flag.value);
  }, [flag.enabled, flag.value]);

  const dirty = enabled !== flag.enabled || value !== flag.value;

  const save = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('feature_flags')
        .update({ enabled, value, updated_at: new Date().toISOString() })
        .eq('key', flag.key);
      if (error) throw error;
    },
    onSuccess: () => {
      // Refetch write-throughs to the sync flag cache used by resolveMediaUrl.
      qc.invalidateQueries({ queryKey: ['feature-flags'] });
      toast({ title: 'Flag saved', description: `${flag.key} updated.` });
    },
    onError: (err) => {
      toast({
        title: 'Save failed',
        description: err instanceof Error ? err.message : 'Could not update flag.',
        variant: 'destructive',
      });
    },
  });

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-mono text-sm font-semibold text-foreground">{flag.key}</p>
            {flag.description && (
              <p className="mt-0.5 text-xs text-muted-foreground">{flag.description}</p>
            )}
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} aria-label={`Toggle ${flag.key}`} />
        </div>

        <div className="flex items-center gap-2">
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={isAudienceFlag(flag.key)
              ? 'beta allowlist: email, email, … (empty = everyone)'
              : 'value (e.g. 20260525a)'}
            className="flex-1"
          />
          <Button
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
            size="sm"
          >
            {save.isPending
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Save className="h-4 w-4" />}
            <span className="ml-1.5">Save</span>
          </Button>
        </div>
        {isAudienceFlag(flag.key) && (
          <p className="mt-1 text-[11px] text-muted-foreground">
            This value is an <b>email allowlist</b>, not a cache-buster. Empty = every member.
            A value with no valid emails restricts to staff only (fails closed).
          </p>
        )}
      </CardContent>
    </Card>
  );
}
